import {
  ClientWorkFlowStatus,
  FrequencyAndScheduleStatus,
  IClientInvoice,
  IClientInvoiceCreateRequest,
  IClientInvoiceTemplate,
  IClientInvoiceTemplateCreateRequest,
  IClientInvoiceTemplateUpdateRequest,
  IClientInvoiceUpdateRequest,
  ICollaboratorSchema,
  ICollaboratorsReportRequest,
  IInvoice,
  IInvoiceCreateRequest,
  IInvoiceFeeCalculation,
  IInvoicePdfGenerationResponse,
  IInvoiceRefundRequest,
  IInvoiceTemplate,
  IInvoiceTemplateCreateRequest,
  IInvoiceTemplateUpdateRequest,
  IInvoiceUpdateRequest,
  IMccResponse,
  InvoiceStatus,
  IPayableCreateRequest,
  IPayableSchema,
  IPayablesSummary,
  IPayableUpdateRequest,
  IPayeeTaxFormResponse,
  IPayerTaxFormResponse,
  IPaymentCard,
  IPaymentCardCreateInitiateResponse,
  IPaymentCardOneTimeCreateRequest,
  IPayoutDestinationUpdate,
  IPayRequest,
  IPayrollSettings,
  IPayrollSettingsUpdate,
  IRecipientVerificationRequest,
  IRecipientVerificationResponse,
  IServiceStatusResponse,
  IServiceUpdateRequest,
  ITaxFormCorrectionRequest,
  ITaxFormCorrectionResponse,
  ITaxFormCreateRequest,
  ITaxFormInviteRequest,
  ITaxFormInviteResponse,
  ITaxFormRedeliveryRequest,
  ITaxFormResponse,
  ITaxFormSubmissionDownloadRequest,
  ITaxFormUpdateRequest,
  ITinVerificationResponse,
  MemberWorkFlowStatus,
  PaymentCardType,
  PayoutDestinationType,
  PayoutPreferences,
  TaxFormType
} from "@wingspanhq/payments/dist/interfaces";
import {
  IDeductionCreateRequest,
  IDeductionResponse,
  IDeductionUpdateRequest
} from "@wingspanhq/payments/dist/interfaces/api/deductions";
import { IPayoutSettingsResponse } from "@wingspanhq/payments/dist/interfaces/api/payoutSettings";
import {
  IContractorPayoutsSummaryReportResponse,
  IContractorReportResponse,
  IInvoiceAgingReportRequest,
  IInvoiceAgingReportResponse,
  ILineItemsAgingReportRequest,
  ILineItemsAgingReportResponse,
  IPayableAgingReportRequest,
  IPayableAgingReportResponse,
  IPayrollReportResponse
} from "@wingspanhq/payments/dist/interfaces/api/reports";
import { TaxFormDocumentType } from "@wingspanhq/payments/dist/interfaces/taxForm";
import { IAddress } from "@wingspanhq/users/dist/lib/interfaces";
import { getInvoiceRow } from "../modules/Invoicing/service";
import { IContractorPayoutsSummaryReportRequest } from "../query/payments/queries";
import { getChangedData } from "../utils/getChangedData";
import { isSameDate, ListRequestQuery, sleep } from "../utils/serviceHelper";
import { servicePayments } from "./servicePayments";

const service = servicePayments;

/* Service */

const getService = async (): Promise<IServiceStatusResponse> => {
  const { data } = await service.get("/service");
  return data;
};

const updateService = async (
  userId: string,
  requestData: IServiceUpdateRequest
): Promise<IServiceStatusResponse> => {
  const { data } = await service.patch(`/service/${userId}`, requestData);

  return data;
};

/* MCC */

const getMCCs = async (): Promise<IMccResponse[]> => {
  const { data } = await service.get("/mcc");
  return data;
};

/* Invoice */

export interface InvoiceListFilter {
  memberId?: string;
  clientId?: string;
  "lineItems.labels.bulkBatchId"?:
    | string
    | {
        in?: string[];
      };
  memberClientId?:
    | string
    | {
        in?: string[];
      };
  status?:
    | InvoiceStatus
    | {
        "!="?: InvoiceStatus;
        in?: InvoiceStatus[];
      };
  "client.workflowStatus"?:
    | ClientWorkFlowStatus
    | {
        "!="?: ClientWorkFlowStatus;
        in?: ClientWorkFlowStatus[];
        notIn?: ClientWorkFlowStatus[];
      };
  "member.workflowStatus"?:
    | MemberWorkFlowStatus
    | {
        "!="?: MemberWorkFlowStatus;
        in?: MemberWorkFlowStatus[];
        notIn?: MemberWorkFlowStatus[];
      };
  dueDate?: {
    [key in "=" | ">" | ">=" | "<" | "<="]?: Date;
  };
  "events.openedAt"?: {
    [key in "=" | ">" | ">=" | "<" | "<="]?: Date;
  };
  "events.paidAt"?: {
    [key in "=" | ">" | ">=" | "<" | "<="]?: Date;
  };
  createdAt?: {
    [key in "=" | ">" | ">=" | "<" | "<="]?: Date;
  };
  updatedAt?: {
    [key in "=" | ">" | ">=" | "<" | "<="]?: Date;
  };
  "labels.invoiceType"?:
    | string
    | {
        "!=": string;
      };
  "labels.creationSource"?:
    | string
    | {
        "!=": string;
      };
  "lineItems.labels.taxForm1099"?: keyof ICollaboratorSchema["form1099Balances"];
  "internal.collaborators.parentInvoiceId"?: string;
  "internal.payroll.payrollId"?: string;
}

interface InvoiceListSort {
  updatedAt?: "asc" | "desc";
  createdAt?: "asc" | "desc";
  "events.paidAt"?: "asc" | "desc";
}

export interface InvoiceListQuery
  extends ListRequestQuery<InvoiceListFilter, InvoiceListSort> {}

export interface InvoiceListStats {
  totalAmount: number;
}

const getInvoices = async (query?: InvoiceListQuery): Promise<IInvoice[]> => {
  const { data } = await service.get("/invoice", { params: query });
  return data;
};

const getInvoicesListSize = async (
  query?: InvoiceListQuery
): Promise<number> => {
  const { headers } = await service.get("/invoice", {
    params: { ...query, page: { size: 1 } }
  });
  return Number(headers["x-ws-list-size"]);
};

const getInvoicesListStats = async (
  query?: InvoiceListQuery
): Promise<InvoiceListStats> => {
  const { headers } = await service.get("/invoice", {
    params: { ...query, page: { size: 1 } }
  });
  return {
    totalAmount: Number(headers["x-ws-custom-invoice-amount-total"])
  };
};

const getInvoice = async (id: string): Promise<IInvoice> => {
  const { data } = await service.get(`/invoice/${id}`);
  return data;
};

const createInvoice = async (
  requestData: IInvoiceCreateRequest
): Promise<IInvoice> => {
  const { data } = await service.post("/invoice", requestData);
  return data;
};

const updateInvoice = async (
  id: string,
  requestData: IInvoiceUpdateRequest,
  config?: {
    waitForRowUpdate?: boolean;
  }
): Promise<IInvoice> => {
  const { data: invoice } = await service.patch<IInvoice>(
    `/invoice/${id}`,
    requestData
  );

  if (config?.waitForRowUpdate) {
    try {
      for (let i = 0; i < 3; i++) {
        const row = await getInvoiceRow(id);
        if (isSameDate(row.updatedAt, invoice.updatedAt)) {
          return invoice;
        }

        await sleep(250);
      }
    } catch (error) {
      console.error("Failed to get invoice row", error);
    }
  }

  return invoice;
};

const sendInvoiceReminder = async (id: string) => {
  const { data } = await service.post(`/invoice/${id}/send`);
  return data;
};

const generateInvoicePdf = async (
  id: string
): Promise<IInvoicePdfGenerationResponse> => {
  const { data } = await service.post(`/invoice/${id}/generate`);
  return data;
};

const refundInvoice = async (
  id: string,
  requestData: IInvoiceRefundRequest
) => {
  const { data } = await service.post(`/invoice/${id}/refund`, requestData);
  return data;
};

const payInvoice = async (id: string) => {
  const { data } = await service.post(`/invoice/${id}/pay`);
  return data;
};

const deleteInvoice = async (
  id: string,
  config?: {
    waitForRowUpdate?: boolean;
  }
) => {
  const { data } = await service.delete(`/invoice/${id}`);

  if (config?.waitForRowUpdate) {
    try {
      for (let i = 0; i < 3; i++) {
        try {
          await getInvoiceRow(id);
        } catch (error) {
          if ((error as any).response?.status === 404) {
            return data;
          }
        }

        await sleep(250);
      }
    } catch (error) {
      console.error("Failed to get invoice row", error);
    }
  }

  return data;
};

/* Payble */

export interface PayablesFilter {
  memberId?: string;
  clientId?: string;
  memberClientId?:
    | string
    | {
        in?: string[];
      };
  status?:
    | InvoiceStatus
    | {
        "!="?: InvoiceStatus;
        in?: InvoiceStatus[];
      };
  "client.workflowStatus"?:
    | ClientWorkFlowStatus
    | {
        "!="?: ClientWorkFlowStatus;
        in?: ClientWorkFlowStatus[];
        notIn?: ClientWorkFlowStatus[];
      };
  "member.workflowStatus"?:
    | MemberWorkFlowStatus
    | {
        "!="?: MemberWorkFlowStatus;
        in?: MemberWorkFlowStatus[];
        notIn?: MemberWorkFlowStatus[];
      };
  dueDate?: {
    [key in "=" | ">" | ">=" | "<" | "<="]?: Date;
  };
  "events.openedAt"?: {
    [key in "=" | ">" | ">=" | "<" | "<="]?: Date;
  };
  "events.paidAt"?: {
    [key in "=" | ">" | ">=" | "<" | "<="]?: Date;
  };
  createdAt?: {
    [key in "=" | ">" | ">=" | "<" | "<="]?: Date;
  };
  updatedAt?: {
    [key in "=" | ">" | ">=" | "<" | "<="]?: Date;
  };
  "labels.invoiceType"?:
    | string
    | {
        "!=": string;
      };
  "labels.creationSource"?:
    | string
    | {
        "!=": string;
      };
  "internal.collaborators.parentInvoiceId"?: string;
  "lineItems.labels.taxForm1099"?:
    | number
    | {
        "="?: number;
        "!="?: number;
        exists?: boolean;
      };
}

export type PayablesSort = {
  updatedAt?: "asc" | "desc";
  "events.paidAt"?: "asc" | "desc";
  "internal.events.paymentDueAt"?: "asc" | "desc";
};

export type PayablesListRequestQuery = ListRequestQuery<
  PayablesFilter,
  PayablesSort
> & {
  excludeDeclined?: "true";
};

export type PayablesWithSummary = {
  data: IPayableSchema[];
  summary: {
    listSize: number;
    totalValue: number;
  };
};

const getPayables = async (
  query?: PayablesListRequestQuery,
  userId?: string
): Promise<PayablesWithSummary> => {
  const { headers, data } = await service.get("/payable", {
    params: query,
    headers: {
      "x-wingspan-user": userId
    }
  });

  return {
    data,
    summary: {
      listSize: Number(headers["x-ws-list-size"]),
      totalValue: Number(headers["x-ws-custom-invoice-amount-total"])
    }
  };
};

const getPayable = async (id: string): Promise<IPayableSchema> => {
  const { data } = await service.get(`/payable/${id}`);
  return data;
};

const createPayable = async (
  requestData: IPayableCreateRequest
): Promise<IPayableSchema> => {
  const { data } = await service.post("/payable", requestData);
  return data;
};

const updatePayable = async (
  id: string,
  requestData: IPayableUpdateRequest
): Promise<IPayableSchema> => {
  const { data } = await service.patch(`/payable/${id}`, requestData);
  return data;
};
const deletePayable = async (id: string) => {
  const { data } = await service.delete(`/payable/${id}`);
  return data;
};

/* Invoice template */

interface InvoiceTemplateListFilter {
  memberId?: string;
  status?:
    | FrequencyAndScheduleStatus
    | {
        "!="?: FrequencyAndScheduleStatus;
        in?: FrequencyAndScheduleStatus[];
      };
}

interface InvoiceTemplateListSort {
  createdAt?: "asc" | "desc";
  updatedAt?: "asc" | "desc";
}

export interface InvoiceTemplateListQuery
  extends ListRequestQuery<
    InvoiceTemplateListFilter,
    InvoiceTemplateListSort
  > {}

const getInvoiceTemplates = async (
  query?: InvoiceTemplateListQuery
): Promise<IInvoiceTemplate[]> => {
  const response = await service.get("/invoice-template", { params: query });
  return response.data;
};

const getInvoiceTemplatesListSize = async (
  query?: InvoiceTemplateListQuery
): Promise<number> => {
  const { headers } = await service.get("/invoice-template", {
    params: { ...query, page: { size: 1 } }
  });
  return Number(headers["x-ws-list-size"]);
};

const getInvoiceTemplate = async (id: string): Promise<IInvoiceTemplate> => {
  const response = await service.get(`/invoice-template/${id}`);
  return response.data;
};

const createInvoiceTemplate = async (
  data: IInvoiceTemplateCreateRequest
): Promise<IInvoiceTemplate> => {
  const response = await service.post("/invoice-template", data);
  return response.data;
};

const updateInvoiceTemplate = async (
  id: string,
  data: IInvoiceTemplateUpdateRequest
): Promise<IInvoiceTemplate> => {
  const response = await service.patch(`/invoice-template/${id}`, data);
  return response.data;
};

const deleteInvoiceTemplate = async (id: string): Promise<IInvoiceTemplate> => {
  const response = await service.delete(`/invoice-template/${id}`);
  return response.data;
};

/* Client Invoice */

const getClientInvoices = async (
  query?: ListRequestQuery<
    InvoiceListFilter,
    {
      updatedAt: "asc" | "desc";
    }
  >
): Promise<IClientInvoice[]> => {
  const response = await service.get("/client/invoice", {
    params: query
  });
  return response.data;
};

const getClientInvoice = async (id: string): Promise<IClientInvoice> => {
  const response = await service.get(`/client/invoice/${id}`);
  return response.data;
};

const createClientInvoice = async (
  data: IClientInvoiceCreateRequest
): Promise<IClientInvoice> => {
  const response = await service.post("/client/invoice", data);
  return response.data;
};

const updateClientInvoice = async (
  id: string,
  data: IClientInvoiceUpdateRequest
): Promise<IClientInvoice> => {
  const response = await service.patch(`/client/invoice/${id}`, data);
  return response.data;
};

const getClientInvoiceFees = async (
  id: string
): Promise<IInvoiceFeeCalculation> => {
  const response = await service.get(`/client/invoice/${id}/fees`);
  return response.data;
};

const payClientInvoice = async (
  id: string,
  data: IPayRequest
): Promise<IClientInvoice> => {
  const response = await service.post(`/client/invoice/${id}/pay`, data);
  return response.data;
};

/* Client Invoice Template */

const getClientInvoiceTemplates = async (): Promise<
  IClientInvoiceTemplate[]
> => {
  const response = await service.get("/client/invoice-template");
  return response.data;
};

const getClientInvoiceTemplate = async (
  id: string
): Promise<IClientInvoiceTemplate> => {
  const response = await service.get(`/client/invoice-template/${id}`);
  return response.data;
};

const createClientInvoiceTemplate = async (
  data: IClientInvoiceTemplateCreateRequest
): Promise<IClientInvoiceTemplate> => {
  const response = await service.post("/client/invoice-template", data);
  return response.data;
};

const updateClientInvoiceTemplate = async (
  id: string,
  data: IClientInvoiceTemplateUpdateRequest
): Promise<IClientInvoiceTemplate> => {
  const response = await service.patch(`/client/invoice-template/${id}`, data);
  return response.data;
};

/* Payables summary */

const getPayablesSummary = async (): Promise<IPayablesSummary> => {
  const response = await service.get("/summary/payables");
  return response.data;
};

/* Payroll settings */
const getPayrollSettings = async (
  memberId: string
): Promise<IPayrollSettings> => {
  const response = await service.get(`/payroll-settings/${memberId}`);
  return response.data;
};

const updatePayrollSettings = async (
  memberId: string,
  data: IPayrollSettingsUpdate
): Promise<IPayrollSettings> => {
  const response = await service.patch(`/payroll-settings/${memberId}`, data);
  return response.data;
};

/* Payout Settings */

const getPayoutSettings = async (
  memberId: string
): Promise<IPayoutSettingsResponse> => {
  const response = await service.get(`/payout-settings/${memberId}`);
  return response.data;
};

const updatePayoutSettings = async (
  memberId: string,
  updateData: {
    payoutPreferences?: PayoutPreferences;
    standard?: {
      destinationType: PayoutDestinationType;
      percentage?: number;
      destinationId: string;
    } | null;
    instant?: {
      destinationType: PayoutDestinationType;
      percentage?: number;
      destinationId: string;
    } | null;
  }
): Promise<IPayoutSettingsResponse> => {
  const payoutSettings = await getPayoutSettings(memberId);

  const payoutDestinationsUpdate: IPayoutDestinationUpdate[] =
    payoutSettings.payoutDestinations.map(_ => ({}));

  // Update standard destination
  if (updateData.standard !== undefined) {
    const indexOfCurrentStandardPayoutMethod =
      payoutSettings.payoutDestinations.findIndex(
        d => d.payoutMethod === PayoutPreferences.Standard
      );

    if (indexOfCurrentStandardPayoutMethod >= 0) {
      if (updateData.standard === null) {
        // Delete standard destination
        payoutDestinationsUpdate[indexOfCurrentStandardPayoutMethod] =
          null as any;
      } else {
        // Update standard destination
        payoutDestinationsUpdate[indexOfCurrentStandardPayoutMethod] =
          getChangedData(
            payoutSettings.payoutDestinations[
              indexOfCurrentStandardPayoutMethod
            ],
            updateData.standard
          );
      }
    } else {
      // Create a standard destination
      if (updateData.standard !== null) {
        payoutDestinationsUpdate.push({
          payoutMethod: PayoutPreferences.Standard,
          percentage: 100,
          ...updateData.standard
        });
      }
    }
  }

  // Update instant destination
  if (updateData.instant !== undefined) {
    const indexOfCurrentInstantPayoutMethod =
      payoutSettings.payoutDestinations.findIndex(
        d => d.payoutMethod === PayoutPreferences.Instant
      );

    if (indexOfCurrentInstantPayoutMethod >= 0) {
      if (updateData.instant === null) {
        // Delete instant destination
        payoutDestinationsUpdate[indexOfCurrentInstantPayoutMethod] =
          null as any;
      } else {
        // Update instant destination
        payoutDestinationsUpdate[indexOfCurrentInstantPayoutMethod] =
          getChangedData(
            payoutSettings.payoutDestinations[
              indexOfCurrentInstantPayoutMethod
            ],
            updateData.instant
          );
      }
    } else {
      // Create instant destination
      if (updateData.instant !== null) {
        payoutDestinationsUpdate.push({
          payoutMethod: PayoutPreferences.Instant,
          percentage: 100,
          ...updateData.instant
        });
      }
    }
  }

  const response = await service.patch(`/payout-settings/${memberId}`, {
    payoutPreferences: updateData.payoutPreferences,
    payoutDestinations: payoutDestinationsUpdate
  });

  return response.data as IPayoutSettingsResponse;
};

const updateWalletDistribution = async (
  memberId: string,
  walletDistributionPercentage: number
): Promise<IPayoutSettingsResponse> => {
  const payoutSettings = await getPayoutSettings(memberId);

  const payoutDestinationsUpdate: (IPayoutDestinationUpdate | null)[] =
    payoutSettings.payoutDestinations.map(_ =>
      walletDistributionPercentage === 100
        ? null
        : {
            percentage: 100 - walletDistributionPercentage
          }
    );

  const response = await service.patch(`/payout-settings/${memberId}`, {
    payoutDestinations: payoutDestinationsUpdate
  });

  return response.data as IPayoutSettingsResponse;
};

// TODO: remove these interfaces and replace with those in payments

export enum CardBrand {
  AmericanExpress = "AmericanExpress",
  Visa = "Visa",
  Mastercard = "Mastercard",
  Discover = "Discover",
  DinersClub = "DinersClub",
  JCB = "JCB"
}

export interface ICheckbookCard {
  cardId: string;
  brand: CardBrand;
  last4: string;
  expirationYYYY: string;
  expirationMM: string;
  address: IAddress;
}

export interface ICheckbookCardCreate {
  cardNumber: string;
  expYYYY: string;
  expMM: string;
  cvv?: string;
  name: string;
  address?: IAddress;
}

// -- remove meeeee ^ --

const getPayoutSettingsDebitCards = async (
  memberId: string
): Promise<ICheckbookCard[]> => {
  const response = await service.get(`/payout-settings/${memberId}/debit-card`);
  return response.data;
};

const getPayoutSettingsDebitCard = async (
  memberId: string,
  debitCardId: string
): Promise<ICheckbookCard> => {
  const response = await service.get(
    `/payout-settings/${memberId}/debit-card/${debitCardId}`
  );
  if (response.data?.length > 0) {
    return response.data[0];
  }
  return response.data;
};

const createPayoutSettingsDebitCard = async (
  memberId: string,
  data: ICheckbookCardCreate
): Promise<ICheckbookCard> => {
  const response = await service.post(
    `/payout-settings/${memberId}/debit-card`,
    data
  );
  return response.data;
};

const deletePayoutSettingsDebitCard = async (
  memberId: string,
  debitCardId: string
): Promise<ICheckbookCard> => {
  const response = await service.delete(
    `/payout-settings/${memberId}/debit-card/${debitCardId}`
  );
  return response.data;
};

/* Deductions */

const createClientDeduction = async (
  data: IDeductionCreateRequest
): Promise<IDeductionResponse> => {
  const response = await service.post(`/client-deduction`, data);
  return response.data;
};

const getClientDeductions = async (
  params: any
): Promise<IDeductionResponse[]> => {
  const response = await service.get(`/client-deduction`, { params });
  return response.data;
};

const getClientDeduction = async (id: string): Promise<IDeductionResponse> => {
  const response = await service.get(`/client-deduction/${id}`);
  return response.data;
};

const updateClientDeduction = async (
  id: string,
  data: IDeductionUpdateRequest
): Promise<IDeductionResponse> => {
  const response = await service.patch(`/client-deduction/${id}`, data);
  return response.data;
};

const deleteClientDeduction = async (
  id: string
): Promise<IDeductionResponse> => {
  const response = await service.delete(`/client-deduction/${id}`);
  return response.data;
};

const createCollaboratorDeduction = async (
  data: IDeductionCreateRequest
): Promise<IDeductionResponse> => {
  const response = await service.post(`/collaborator-deduction`, data);
  return response.data;
};

const getCollaboratorDeductions = async (
  params: any
): Promise<IDeductionResponse[]> => {
  const response = await service.get(`/collaborator-deduction`, { params });
  return response.data;
};

const getCollaboratorDeduction = async (
  id: string
): Promise<IDeductionResponse> => {
  const response = await service.get(`/collaborator-deduction/${id}`);
  return response.data;
};

const updateCollaboratorDeduction = async (
  id: string,
  data: IDeductionUpdateRequest
): Promise<IDeductionResponse> => {
  const response = await service.patch(`/collaborator-deduction/${id}`, data);
  return response.data;
};

const deleteCollaboratorDeduction = async (
  id: string
): Promise<IDeductionResponse> => {
  const response = await service.delete(`/collaborator-deduction/${id}`);
  return response.data;
};

const getCollaboratorsReports = async (
  request: ICollaboratorsReportRequest,
  query?: ListRequestQuery
): Promise<{
  data: IContractorReportResponse[];
  summary: {
    listSize: number;
  };
}> => {
  const { data, headers } = await service.get("/reports/collaborators", {
    params: {
      ...request,
      ...query
    }
  });

  return {
    data,
    summary: {
      listSize: Number(headers["x-ws-list-size"])
    }
  };
};

const getCollaboratorPayableSummaryReports = async (
  request: IContractorPayoutsSummaryReportRequest,
  query?: ListRequestQuery
): Promise<{
  data: IContractorPayoutsSummaryReportResponse[];
  summary: {
    listSize: number;
  };
}> => {
  const { data, headers } = await service.get(
    "/reports/collaborators/payables-summary",
    {
      params: {
        ...request,
        ...query
      }
    }
  );

  return {
    data,
    summary: {
      listSize: Number(headers["x-ws-list-size"])
    }
  };
};

const payApproved = async (): Promise<IInvoice> => {
  const response = await service.post("/pay-approved");
  return response.data;
};

const getImmediatePayrollPayabelsList = async (
  query?: ListRequestQuery<
    undefined,
    {
      updatedAt?: "asc" | "desc";
    }
  >
): Promise<PayablesWithSummary> => {
  const { headers, data } = await service.get("/payroll/immediate/payable", {
    params: query
  });

  return {
    data,
    summary: {
      listSize: Number(headers["x-ws-list-size"]),
      totalValue: Number(headers["x-ws-custom-invoice-amount-total"])
    }
  };
};

const getPayrollReports = async (
  query?: ListRequestQuery<{
    invoiceId?:
      | string
      | {
          in?: string[];
        };
  }>
): Promise<{
  data: IPayrollReportResponse;
  summary: {
    listSize: number;
  };
}> => {
  const { data, headers } = await service.get(`/reports/payroll`, {
    params: {
      ...query
    }
  });

  return {
    data,
    summary: {
      listSize: Number(headers["x-ws-list-size"])
    }
  };
};

const getOpenPayableAgingReport = async (
  request: IPayableAgingReportRequest,
  query?: ListRequestQuery
): Promise<{
  data: IPayableAgingReportResponse[];
  summary: {
    listSize: number;
  };
}> => {
  const { data, headers } = await service.get(`reports/aging/payables`, {
    params: {
      ...request,
      ...query
    }
  });

  return {
    data,
    summary: {
      listSize: Number(headers["x-ws-list-size"])
    }
  };
};

const getOpenReceivableAgingReport = async (
  request: IInvoiceAgingReportRequest,
  query?: ListRequestQuery
): Promise<{
  data: IInvoiceAgingReportResponse[];
  summary: {
    listSize: number;
  };
}> => {
  const { data, headers } = await service.get(`/reports/aging/invoices`, {
    params: {
      ...request,
      ...query
    }
  });

  return {
    data,
    summary: {
      listSize: Number(headers["x-ws-list-size"])
    }
  };
};

const getOpenLineItemAgingReport = async (
  request: ILineItemsAgingReportRequest,
  query?: ListRequestQuery
): Promise<{
  data: ILineItemsAgingReportResponse[];
  summary: {
    listSize: number;
  };
}> => {
  const { data, headers } = await service.get(`reports/aging/line-items`, {
    params: {
      ...request,
      ...query
    }
  });

  return {
    data,
    summary: {
      listSize: Number(headers["x-ws-list-size"])
    }
  };
};

const createTaxForm = async (
  requestData: ITaxFormCreateRequest
): Promise<IPayerTaxFormResponse> => {
  const { data } = await service.post("/tax-form", requestData);
  return data;
};

const getTaxForm = async (taxFormId: string) => {
  const { data } = await service.get(`/tax-form/${taxFormId}`);

  // can be one IPayeeTaxFormResponse or IPayerTaxFormResponse;
  return data as any;
};

const updateTaxForm = async (
  taxFormId: string,
  requestData: ITaxFormUpdateRequest,
  params?: {
    skipEventHistory?: boolean;
  }
): Promise<IPayerTaxFormResponse> => {
  const { data } = await service.patch(`/tax-form/${taxFormId}`, requestData, {
    params: {
      skipEventHistory: params?.skipEventHistory ? "true" : undefined
    }
  });
  return data;
};

const downloadTaxFormDocument = async (
  requestData: {
    taxFormId: string;
    submissionId: string;
    // TODO: check backend
    documentType?: TaxFormDocumentType;
  } & ITaxFormSubmissionDownloadRequest
): Promise<Blob> => {
  const { taxFormId, submissionId, ...request } = requestData;

  const { data } = await service.post(
    `/tax-form/${requestData.taxFormId}/submission/${requestData.submissionId}/download`,
    request,
    {
      responseType: "blob"
    }
  );
  return data;
};

const taxFormVerifyIdentity = async (
  taxFormId: string,
  request: IRecipientVerificationRequest
) => {
  const { data } = await service.post(
    `/tax-form/${taxFormId}/verify-identity`,
    request
  );

  return data as IRecipientVerificationResponse;
};

const taxFormVerifyTin = async () => {
  const { data } = await service.post(`/tax-form/verify-tin`, {});

  return data as ITinVerificationResponse;
};

const taxFormSubmitW9 = async () => {
  const { data } = await service.post(`/tax-form/submit-w9`, {});

  return data; //TODO IVerifications;
};

const getPayeeTaxForms = async (year: number, type?: TaxFormType) => {
  const { data } = (await service.get(`/tax-form`, {
    params: {
      year,
      type,
      viewer: "payee"
    }
  })) as { data: IPayeeTaxFormResponse[] };

  return data;
};

const getPayerTaxForms = async (
  year: number,
  type?: TaxFormType,
  xWingspanExpansion?: string,
  params?: any
): Promise<{
  data: IPayerTaxFormResponse[];
  summary: { listSize: number };
}> => {
  const { data, headers } = await service.get("/tax-form", {
    params: {
      year,
      type,
      viewer: "payer",
      ...params
    },
    headers: {
      "x-wingspan-expansion": xWingspanExpansion
    }
  });

  return { data, summary: { listSize: Number(headers["x-ws-list-size"]) } };
};

const request1099Link = async (request: ITaxFormInviteRequest) => {
  const { data } = await service.post(`/tax-form/resend-invite`, request);

  return data as ITaxFormInviteResponse;
};

const resyncTaxForm = async (taxFormId: string) => {
  const { data } = await service.post(`/tax-form/${taxFormId}/resync`, {});

  return data as IPayerTaxFormResponse;
};

const resendEmailCopy = async (
  taxFormId: string,
  request: ITaxFormRedeliveryRequest
) => {
  const { data } = await service.post(
    `/tax-form/${taxFormId}/redeliver`,
    request
  );

  return data as ITaxFormResponse;
};

const createCorrection = async (
  taxFormId: string,
  request: ITaxFormCorrectionRequest
) => {
  const { data } = await service.post(
    `/tax-form/${taxFormId}/correction`,
    request
  );

  return data as ITaxFormCorrectionResponse;
};

const updateCorrection = async (
  taxFormId: string,
  correctionId: string,
  request: ITaxFormCorrectionRequest
) => {
  const { data } = await service.patch(
    `/tax-form/${taxFormId}/correction/${correctionId}`,
    request
  );

  return data as ITaxFormCorrectionResponse;
};

export interface ITaxFormCorrectionRejectRequest {
  payerOwnedData?: {
    comment?: string;
    commentInternal?: string;
  };
}

const rejectCorrection = async (
  taxFormId: string,
  correctionId: string,
  request: ITaxFormCorrectionRejectRequest
) => {
  const { data } = await service.post(
    `/tax-form/${taxFormId}/correction/${correctionId}/reject`,
    request
  );

  return data as ITaxFormCorrectionResponse;
};

const approveCorrection = async (
  taxFormId: string,
  correctionId: string,
  request: ITaxFormCorrectionRejectRequest
) => {
  const { data } = await service.post(
    `/tax-form/${taxFormId}/correction/${correctionId}/submit`,
    request
  );

  return data as ITaxFormCorrectionResponse;
};

const paymentCardInitiateCreate = async () => {
  const { data } = await service.post("/payment-card");

  return data as IPaymentCardCreateInitiateResponse;
};

export type IPaymentCardCompleteCreateRequest = { saveForFutureUse?: boolean };

const paymentCardCompleteCreate = async (
  paymentCardId: string,
  requestData?: IPaymentCardCompleteCreateRequest
) => {
  const { data } = await service.post(
    `/payment-card/${paymentCardId}`,
    requestData
  );

  return data as IPaymentCard;
};

const paymentCardOneTimeInitiateCreate = async (
  requestData: IPaymentCardOneTimeCreateRequest
) => {
  const { data } = await service.post("/payment-card/one-time", requestData);

  return data as IPaymentCardCreateInitiateResponse;
};

const paymentCardOneTimeCompleteCreate = async (
  paymentCardId: string,
  requestData: IPaymentCardOneTimeCreateRequest
) => {
  const { data } = await service.post(
    `/payment-card/one-time/${paymentCardId}`,
    requestData
  );

  return data as IPaymentCard;
};

export type PaymentCardListQuery = ListRequestQuery<{
  cartType: PaymentCardType;
}>;

const paymentCardGet = async (paymentCardId: string) => {
  const { data } = await service.get("/payment-card/" + paymentCardId);

  return data as IPaymentCard;
};

const paymentCardList = async (query?: PaymentCardListQuery) => {
  const { data } = await service.get("/payment-card", {
    // TODO: uncomment when API is ready
    // params: query
  });

  return data as IPaymentCard[];
};

const paymentCardDelete = async (paymentCardId: string) => {
  const { data } = await service.delete(`/payment-card/${paymentCardId}`);

  return data as IPaymentCard;
};

export const paymentsService = {
  service: {
    get: getService,
    update: updateService
  },
  mcc: {
    list: getMCCs
  },
  invoice: {
    list: getInvoices,
    listSize: getInvoicesListSize,
    listStats: getInvoicesListStats,
    get: getInvoice,
    create: createInvoice,
    update: updateInvoice,
    delete: deleteInvoice,
    sendReminder: sendInvoiceReminder,
    generatePdf: generateInvoicePdf,
    refund: refundInvoice,
    pay: payInvoice
  },
  invoiceTemplate: {
    list: getInvoiceTemplates,
    listSize: getInvoiceTemplatesListSize,
    get: getInvoiceTemplate,
    create: createInvoiceTemplate,
    update: updateInvoiceTemplate,
    delete: deleteInvoiceTemplate
  },
  payable: {
    list: getPayables,
    get: getPayable,
    create: createPayable,
    update: updatePayable,
    delete: deletePayable
  },
  client: {
    invoice: {
      list: getClientInvoices,
      get: getClientInvoice,
      create: createClientInvoice,
      update: updateClientInvoice,
      getFees: getClientInvoiceFees,
      pay: payClientInvoice
    },
    invoiceTemplate: {
      list: getClientInvoiceTemplates,
      get: getClientInvoiceTemplate,
      create: createClientInvoiceTemplate,
      update: updateClientInvoiceTemplate
    }
  },
  reports: {
    payroll: getPayrollReports,
    collaborators: getCollaboratorsReports,
    openPayableAging: getOpenPayableAgingReport,
    openReceivableAging: getOpenReceivableAgingReport,
    openLineItemAging: getOpenLineItemAgingReport,
    collaboratorPayable: getCollaboratorPayableSummaryReports
  },
  summary: {
    payables: getPayablesSummary
  },
  payrollSettings: {
    get: getPayrollSettings,
    update: updatePayrollSettings
  },
  payoutSettings: {
    get: getPayoutSettings,
    update: updatePayoutSettings,
    updateWalletDistribution,
    debitCard: {
      get: getPayoutSettingsDebitCard,
      list: getPayoutSettingsDebitCards,
      create: createPayoutSettingsDebitCard,
      delete: deletePayoutSettingsDebitCard
    }
  },
  collaboratorDeductions: {
    list: getCollaboratorDeductions,
    get: getCollaboratorDeduction,
    update: updateCollaboratorDeduction,
    create: createCollaboratorDeduction,
    delete: deleteCollaboratorDeduction
  },
  clientDeductions: {
    list: getClientDeductions,
    get: getClientDeduction,
    update: updateClientDeduction,
    create: createClientDeduction,
    delete: deleteClientDeduction
  },
  payApproved,
  payroll: {
    immediate: {
      payable: {
        list: getImmediatePayrollPayabelsList
      }
    }
  },
  taxForm: {
    create: createTaxForm,
    get: getTaxForm,
    update: updateTaxForm,
    verifyIdentity: taxFormVerifyIdentity,
    verifyTin: taxFormVerifyTin,
    submitW9: taxFormSubmitW9,
    payeeList: getPayeeTaxForms,
    payerList: getPayerTaxForms,
    requestLink: request1099Link,
    resync: resyncTaxForm,
    downloadDocument: downloadTaxFormDocument,
    resendEmailCopy,
    createCorrection,
    updateCorrection,
    rejectCorrection,
    approveCorrection
  },
  paymentCard: {
    initiateCreate: paymentCardInitiateCreate,
    completeCreate: paymentCardCompleteCreate,
    get: paymentCardGet,
    list: paymentCardList,
    delete: paymentCardDelete,
    oneTime: {
      initiateCreate: paymentCardOneTimeInitiateCreate,
      completeCreate: paymentCardOneTimeCompleteCreate
    }
  }
};
